import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import DataTable, {
  CellPrimary,
  ActionButtons,
  EyeIcon,
  DeleteIcon,
} from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import YearSelect from 'components/FormFields/YearSelect';
import { DeleteConfirmModal } from 'components/FormModal';
import PageContainer from 'components/PageContainer';
import PageToolbar from 'components/PageToolbar';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import DeleteCredit from 'pages/app/Credits/OpenCredits/gql/delete-credit';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { logDevError } from 'shared/alerts';
import { AppContext } from 'shared/AppContext';
import CreditDetailModal from './components/CreditDetailModal';
import SelectUserModal from './components/SelectUserModal';
import { useGetUserOpenCreditsByAccount } from './gql/get-user-open-credits-by-account';
import useUserCreditsExport from './hooks/useUserCreditsExport';

// Customer-style header components
const CustomerHeaderContainer = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'white'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)'};
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px;
  padding: 32px;
  margin-bottom: 30px;
  backdrop-filter: ${(props) =>
    props.theme.name === 'dark' ? 'blur(20px)' : 'none'};
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 20px;
  }
`;

const CustomerHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const CustomerAvatar = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(250, 70, 22, 0.1);
  border: 2px solid ${(props) => props.theme?.colors?.primary || '#fa4616'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 20px;
  }
`;

const CustomerInfo = styled.div`
  flex: 1;
`;

const CustomerName = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  letter-spacing: -0.5px;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 24px;
    margin: 0 0 4px 0;
  }
`;

const PageLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CustomerHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    width: 100%;
    align-items: flex-start;
  }
`;

const TotalCreditsValue = styled.div`
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const CreditsAmount = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const CreditsLabel = styled.div`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

// Mobile action button styled component
const MobileActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Dollar icon for transaction button (defined outside component to prevent recreation on render)
const DollarIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
  </svg>
);

const TrackOpenCreditsDetail = (props) => {
  const { deleteCredit } = props;
  const location = useLocation();
  const { id } = useParams();
  const userId = parseInt(id);

  const [credits, setCredits] = useState([]);
  const [count, setCount] = useState(0);
  const [totalCredit, setTotalCredit] = useState('$0.00');
  const [userName, setUserName] = useState('User');
  const [showExportModal, setShowExportModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);
  const [showIssueCredit, setShowIssueCredit] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [shouldDisplayDeleteConfirmation, setShouldDisplayDeleteConfirmation] =
    useState(false);
  const [creditToDelete, setCreditToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);

  const { pathname } = location;

  // Determine route prefix based on current path
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { accountId, capabilities },
  } = useContext(AppContext);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Year filter - use URL param directly
  const thisYear = Number(moment().format('YYYY'));
  const yearFilter = input.year ? Number(input.year) : thisYear;

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    updateQuery({ year: newYear, page: 1 });
  };

  // Fetch credits data
  const { res, loading, refetch } = useGetUserOpenCreditsByAccount();

  // Export hook
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useUserCreditsExport();

  useEffect(() => {
    if (res && res.getUserOpenCreditsByAccount) {
      setCredits(res.getUserOpenCreditsByAccount.results || []);
      setCount(res.getUserOpenCreditsByAccount.count || 0);
      setTotalCredit(res.getUserOpenCreditsByAccount.totalCredit || '$0.00');
      // Use top-level user from response (persists across year changes)
      if (res.getUserOpenCreditsByAccount.user?.formatted_name) {
        setUserName(res.getUserOpenCreditsByAccount.user.formatted_name);
      }
    }
  }, [res]);

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle delete credit
  const handleDeleteClick = (credit) => {
    if (!capabilities || capabilities?.issue_credits) {
      setCreditToDelete({ id: credit.id, user_id: credit.user.id });
      setShouldDisplayDeleteConfirmation(true);
    } else {
      setShowPermissionModal(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!creditToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteCredit(
        { ...creditToDelete, user_id: userId },
        {
          account_id: Number(accountId),
          user_id: userId,
          year: yearFilter,
        }
      );
      if (response && !response.errors) {
        toast.success('Credit deleted successfully');
        setShouldDisplayDeleteConfirmation(false);
        setCreditToDelete(null);
        refetch();
      } else {
        toast.error('Error Deleting Credit');
      }
    } catch (error) {
      logDevError(error);
      toast.error('Error Deleting Credit');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle export
  const handleExport = useCallback(
    async (format) => {
      setShowExportModal(false);

      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        const filters = {
          ...(yearFilter && { year: yearFilter }),
        };

        const jobId = await startExport(accountId, userId, filters, format);

        subscribeToProgress(jobId, {
          onProgress: (status) => {
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} credits (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              const downloadUrl = await getDownloadUrl(jobId);

              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } credits exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export from this account';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        setToasterData({
          title: 'Export Failed',
          message: 'Unable to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      accountId,
      userId,
      yearFilter,
      startExport,
      subscribeToProgress,
      getDownloadUrl,
    ]
  );

  // DataTable columns configuration
  const columns = [
    {
      field: 'amount',
      header: 'Credit',
      mobileTitle: true,
      render: (credit) => <CellPrimary>{credit.amount}</CellPrimary>,
    },
    {
      field: 'date',
      header: 'Issued',
      render: (credit) =>
        moment(credit.date, 'MM-DD-YYYY')
          .format('ddd MMM D - YYYY')
          .toUpperCase(),
    },
    {
      field: 'expiration',
      header: 'Expires',
      render: (credit) =>
        credit.expiration
          ? moment(credit.expiration, 'MM-DD-YYYY')
              .format('ddd MMM D - YYYY')
              .toUpperCase()
          : 'No Expiration',
    },
    {
      field: 'issued_by',
      header: 'Issued By',
      render: (credit) => credit.issued_by?.formatted_name || '',
    },
  ];

  // Custom actions renderer to conditionally show transaction button
  const actionsRenderer = (credit) => (
    <ActionButtons>
      {credit.transaction?.id && (
        <ButtonV2
          variant="icon"
          icon={<DollarIcon />}
          title="View Transaction"
          as={Link}
          to={`${routePrefix}/transactions/detail/${credit.transaction.id}`}
        />
      )}
      <ButtonV2
        variant="icon"
        icon={<EyeIcon />}
        title="View Details"
        onClick={() => setSelectedCredit(credit)}
      />
      <ButtonV2
        variant="icon"
        icon={<DeleteIcon />}
        title="Delete Credit"
        onClick={() => handleDeleteClick(credit)}
      />
    </ActionButtons>
  );

  // Mobile actions renderer with labeled buttons
  const mobileActionsRenderer = (credit) => (
    <>
      {credit.transaction?.id && (
        <MobileActionBtn
          as={Link}
          to={`${routePrefix}/transactions/detail/${credit.transaction.id}`}
        >
          <DollarIcon />
          Transaction
        </MobileActionBtn>
      )}
      <MobileActionBtn onClick={() => setSelectedCredit(credit)}>
        <EyeIcon />
        View Details
      </MobileActionBtn>
      <MobileActionBtn onClick={() => handleDeleteClick(credit)}>
        <DeleteIcon />
        Delete
      </MobileActionBtn>
    </>
  );

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Customer-style Header */}
        <CustomerHeaderContainer>
          <CustomerHeaderLeft>
            <CustomerAvatar>{getInitials(userName)}</CustomerAvatar>
            <CustomerInfo>
              <CustomerName>{userName}</CustomerName>
              <PageLabel>Open Credits</PageLabel>
            </CustomerInfo>
          </CustomerHeaderLeft>
          <CustomerHeaderRight>
            <TotalCreditsValue>
              <CreditsAmount>{totalCredit}</CreditsAmount>
              <CreditsLabel>Total Open Credits</CreditsLabel>
            </TotalCreditsValue>
          </CustomerHeaderRight>
        </CustomerHeaderContainer>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Page Header with Search */}
                <PageToolbar>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search by Issuer"
                    debounceMs={500}
                  />
                  <YearSelect
                    value={yearFilter}
                    onChange={handleYearChange}
                  />
                </PageToolbar>

                {/* DataTable */}
                <DataTable
                  columns={columns}
                  data={credits}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No credits found"
                  emptySubtext="Try adjusting your search or filters"
                  actionsRenderer={actionsRenderer}
                  mobileActionsRenderer={mobileActionsRenderer}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Issue Credit',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                  ),
                  onClick: () =>
                    !capabilities || capabilities?.issue_credits
                      ? setShowIssueCredit(true)
                      : setShowPermissionModal(true),
                },
                {
                  label: 'Export Report',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => setShowExportModal(true),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          recordCount={count}
          dataType="credits"
        />

        {/* Select User Modal for Issue Credit */}
        <SelectUserModal
          isOpen={showIssueCredit}
          onClose={() => setShowIssueCredit(false)}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={shouldDisplayDeleteConfirmation}
          onClose={() => {
            setShouldDisplayDeleteConfirmation(false);
            setCreditToDelete(null);
          }}
          title="Delete Credit"
          message="Are you sure you want to delete this credit? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />

        {/* Permission Modal */}
        <PermissionModal
          isVisible={showPermissionModal}
          hideModal={() => setShowPermissionModal(false)}
        />

        {/* Credit Detail Modal */}
        <CreditDetailModal
          isOpen={!!selectedCredit}
          onClose={() => setSelectedCredit(null)}
          credit={selectedCredit}
          routePrefix={routePrefix}
        />

        {/* Toaster */}
        {toasterData && (
          <Toaster
            isOpen={!!toasterData}
            onClose={() => setToasterData(null)}
            title={toasterData.title}
            message={toasterData.message}
            type={toasterData.type}
            duration={toasterData.persistent ? 25000 : 10000}
            persistent={toasterData.persistent}
            action={toasterData.action}
          />
        )}
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default DeleteCredit(TrackOpenCreditsDetail);
